import React, {useMemo} from 'react';
import {NestedCard, FlexRow, Text, SubHeading, FlexColumn, Icon, Frame, useTableFunctions} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import Util from '../../forecast-app/shared/util/util';
import {
	EyeOptionMap,
	Phase,
	Company,
	ExpenseRow,
	RoleRow,
	ExpenseRowUpdate,
	RoleRowUpdate,
	ProjectPhaseDeliverable,
	GroupedBaselineTotals,
	ContextMenuOption,
	DeliverableEdges,
	Deliverable,
} from './BaselineTypes';
import {useState, useEffect} from 'react';
import {idFromGlobalId} from '../../forecast-app/shared/util/GlobalIdUtil';
import {getTotals} from './BaselineEditPage';
import {uniqueId} from 'lodash';
import {TimeEstimatesTable} from './TimeEstimatesTable';
import {ExpensesEstimatesTable} from './ExpensesEstimateTable';
import styled from 'styled-components';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';

const SummaryCard = (props: {label: string; value: string}) => (
	<FlexColumn>
		<Text color="auxiliary" size={'2'}>
			{props.label}
		</Text>
		<SubHeading size={'2'}>{props.value}</SubHeading>
	</FlexColumn>
);

const SummaryContainer = styled(FlexRow)`
	padding: 8px;
`;

const renormaliseRoleRevenues = (updatedRows: (RoleRow | ExpenseRow)[], multiplier: number) =>
	updatedRows.map(row => {
		if ('phaseBaselineRoleId' in row) {
			const revenue = row.valueOfService * multiplier;
			const profit = revenue - row.cost;
			const margin = revenue ? Util.roundFloatToTwoDecimals(profit / revenue) : 0;
			return {...row, revenue, profit, margin};
		}
		return row;
	});

const AddButtonWrapper = styled.div`
	padding-top: 8px;
	width: fit-content;
`;

const TableTitleWrapper = styled(FlexRow)`
	margin-bottom: 8px;
`;

const AddButton = (props: {text: string; onClick: () => void}) => (
	<AddButtonWrapper onClick={props.onClick} style={{cursor: 'pointer'}}>
		<FlexRow>
			<Icon icon="plus" size="m" color="primary"></Icon>
			<SubHeading color={'primary'}>{props.text}</SubHeading>
		</FlexRow>
	</AddButtonWrapper>
);

export const DeliverableCard = ({
	phaseDeliverable,
	currency,
	eyeOptionMap,
	editMode,
	company,
	phase,
	setProjectBaselineItems,
	baselineItems,
	groupedProjectTotals,
	projectBudget,
	isFixedPrice,
	projectDeliverables,
	setPhaseDeliverables,
}: {
	phaseDeliverable?: ProjectPhaseDeliverable;
	currency: string;
	company: Company;
	eyeOptionMap?: EyeOptionMap;
	editMode?: boolean;
	phase?: Phase;
	setProjectBaselineItems?: React.Dispatch<React.SetStateAction<(RoleRow | ExpenseRow)[]>>;
	setPhaseDeliverables?: React.Dispatch<React.SetStateAction<ProjectPhaseDeliverable[]>>;
	baselineItems?: (RoleRow | ExpenseRow)[];
	groupedProjectTotals?: GroupedBaselineTotals;
	projectBudget?: number;
	isFixedPrice?: boolean;
	projectDeliverables?: DeliverableEdges;
}): JSX.Element => {
	const {formatMessage, formatNumber} = useIntl();
	const {financialNumbers, deliverable} = phaseDeliverable || {};

	const [rolesTableRef] = useTableFunctions();
	const [expensesTableRef] = useTableFunctions();

	const [phaseDeliverableBaselineTotals, setPhaseDeliverableBaselineTotals] = useState(financialNumbers);

	const {baselineCost, baselineMargin, baselineMinutes, baselineProfit, baselineRevenue, baselineTimeAndExpenses} =
		phaseDeliverableBaselineTotals || {};

	useEffect(() => {
		const updatedTotals = getTotals(baselineItems || []);
		setPhaseDeliverableBaselineTotals(updatedTotals);
	}, [baselineItems]);

	const formatCurrency = (value: number) =>
		formatNumber(value, {style: 'currency', currency, currencyDisplay: 'narrowSymbol'});

	const [roundedHours, roundedvalueOfService, roundedRevenue, roundedCost, roundedprofit]: number[] = useMemo(
		() =>
			[
				(baselineMinutes || 0) / 60,
				baselineTimeAndExpenses || 0,
				baselineRevenue || 0,
				baselineCost || 0,
				baselineProfit || 0,
			].map(Util.roundFloatToTwoDecimals),
		[phaseDeliverableBaselineTotals]
	);

	const roundedMargin = Math.trunc((baselineMargin || 0) * 100);

	const hours = `${roundedHours}h`;
	const valueOfService = formatCurrency(roundedvalueOfService);
	const revenue = formatCurrency(roundedRevenue);
	const cost = formatCurrency(roundedCost);
	const profit = formatCurrency(roundedprofit);
	const margin = `${roundedMargin.toString()}%`;

	/* roles */

	const emptyRole = (): RoleRow => ({
		phaseBaselineRoleId: uniqueId(),
		projectId: phase?.projectId || '',
		phaseId: phase?.id || '',
		deliverableId: phaseDeliverable?.deliverable?.id || '',
		roleName: '',
		roleId: '',
		hours: 0,
		valueOfService: 0,
		revenue: 0,
		cost: 0,
		profit: 0,
		ratePerHour: 0,
		costPerHour: 0,
	});

	const rolesData = baselineItems?.filter(item => item && 'phaseBaselineRoleId' in item) as RoleRow[];

	const timeEstimatesSubHeading = `${formatMessage({id: 'deliverables.edit_baseline.time_estimates'})} (${
		rolesData?.length || 0
	})`;

	const hasTimeTable = rolesData?.length > 0;

	const recalculateRoleRows = (row: RoleRow) => {
		const {hours, costPerHour, ratePerHour} = row;

		const valueOfService = ratePerHour * hours;
		const cost = costPerHour * hours;
		const revenue = valueOfService;
		const profit = revenue - cost;

		const updateFields: RoleRowUpdate = {valueOfService, cost, profit, revenue};

		const newRow = {...row, ...updateFields};

		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => {
				const rowIndex = old.findIndex(
					item => 'phaseBaselineRoleId' in item && item.phaseBaselineRoleId === row.phaseBaselineRoleId
				);

				const updatedRows = [...old.slice(0, rowIndex), newRow, ...old.slice(rowIndex + 1)];

				if (projectBudget !== undefined && isFixedPrice) {
					const oldRow = old[rowIndex] as RoleRow;

					const valueOfServiceDiff = valueOfService - oldRow.valueOfService;

					const newTotalRolesValueOfService =
						(groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses || 0) + valueOfServiceDiff;

					const multiplier = newTotalRolesValueOfService
						? (projectBudget - (groupedProjectTotals?.expensesTotals.baselineRevenue || 0)) /
						  newTotalRolesValueOfService
						: 1;

					return renormaliseRoleRevenues(updatedRows, multiplier);
				}
				return updatedRows;
			});
		}
	};

	const onRoleSelect = (roleId: string, row: RoleRow) => {
		const convertedRoleId = idFromGlobalId(roleId);
		const averageRate =
			phase && phase.averageRatesByRole ? phase?.averageRatesByRole.find(rate => rate?.roleId === convertedRoleId) : null;

		const role = company?.roles?.edges?.find(edge => edge?.node?.id === roleId);
		if (role && role.node) {
			const newRow = row;
			newRow.roleId = role.node.id;
			newRow.roleName = role.node.name || '';
			newRow.ratePerHour = averageRate && averageRate.rate ? averageRate.rate : 0;
			newRow.costPerHour = averageRate && averageRate.costRate ? averageRate.costRate : 0;
			recalculateRoleRows(newRow);
		}
	};

	const onRoleAdd = () => {
		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => {
				return [...old, emptyRole()];
			});
		}
	};

	const onDeleteRoleRow = (row: RoleRow) => {
		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => {
				const rowIndex = old.findIndex(
					item => 'phaseBaselineRoleId' in item && item.phaseBaselineRoleId === row.phaseBaselineRoleId
				);

				const updatedRows = [...old.slice(0, rowIndex), ...old.slice(rowIndex + 1)];

				if (projectBudget !== undefined && isFixedPrice) {
					const oldRow = old[rowIndex] as RoleRow;

					const totalRolesValueOfService =
						(groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses || 0) - oldRow.valueOfService;

					const multiplier = totalRolesValueOfService
						? (projectBudget - (groupedProjectTotals?.expensesTotals.baselineRevenue || 0)) /
						  totalRolesValueOfService
						: 1;

					return renormaliseRoleRevenues(updatedRows, multiplier);
				}

				return updatedRows;
			});
		}
	};

	/* expenses */

	const emptyExpense = (): ExpenseRow => ({
		phaseBaselineExpenseId: uniqueId(),
		projectId: phase?.projectId || '',
		phaseId: phase?.id || '',
		deliverableId: phaseDeliverable?.deliverable?.id || '',
		name: '',
		categoryName: '',
		categoryId: '',
		revenue: 0,
		cost: 0,
		markup: 0,
		profit: 0,
		margin: 0,
	});

	const expensesData = baselineItems?.filter(item => item && 'phaseBaselineExpenseId' in item) as ExpenseRow[];

	const expenseEstimatesSubHeading = `${formatMessage({id: 'deliverables.edit_baseline.expense_estimates'})} (${
		expensesData?.length || 0
	})`;

	const hasExpensesTable = expensesData?.length > 0;

	const recalculateExpenseRows = (row: ExpenseRow, changedField: string) => {
		let revenue, cost, markup;
		if (changedField === 'revenue' || changedField === 'cost') {
			revenue = row.revenue;
			cost = row.cost;
			markup = cost ? (revenue - cost) / cost : 0;
		} else {
			cost = row.cost;
			markup = row.markup;
			revenue = cost + markup * cost;
		}

		const profit = revenue - cost;
		const margin = revenue ? profit / revenue : 0;

		const updateFields: ExpenseRowUpdate = {profit, margin, markup, revenue, cost};

		const newRow = {...row, ...updateFields};

		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => {
				const rowIndex = old.findIndex(
					item => 'phaseBaselineExpenseId' in item && item.phaseBaselineExpenseId === row.phaseBaselineExpenseId
				);
				const updatedRows = [...old.slice(0, rowIndex), newRow, ...old.slice(rowIndex + 1)];

				if (projectBudget !== undefined && isFixedPrice) {
					const oldRow = old[rowIndex] as RoleRow;

					const revenueDiff = revenue - oldRow.revenue;
					const newTotalExpensesRevenue = (groupedProjectTotals?.expensesTotals.baselineRevenue || 0) + revenueDiff;

					const multiplier = groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
						? (projectBudget - newTotalExpensesRevenue) / groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
						: 1;

					return renormaliseRoleRevenues(updatedRows, multiplier);
				}
				return updatedRows;
			});
		}
	};

	const onExpenseCategorySelect = (categoryId: string, row: ExpenseRow) => {
		const category = company?.expenseCategories?.edges?.find(edge => edge?.node?.id === categoryId);

		if (category && category.node && category.node.name) {
			const newRow = {...row, categoryId, categoryName: category.node.name};

			if (setProjectBaselineItems) {
				setProjectBaselineItems(old => {
					const rowIndex = old.findIndex(
						item => 'phaseBaselineExpenseId' in item && item.phaseBaselineExpenseId === row.phaseBaselineExpenseId
					);
					return [...old.slice(0, rowIndex), newRow, ...old.slice(rowIndex + 1)];
				});
			}
		}
	};

	const onExpenseAdd = () => {
		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => {
				return [...old, emptyExpense()];
			});
		}
	};

	const onDeleteExpenseRow = (row: ExpenseRow) => {
		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => {
				const rowIndex = old.findIndex(
					item => 'phaseBaselineExpenseId' in item && item.phaseBaselineExpenseId === row.phaseBaselineExpenseId
				);
				const updatedRows = [...old.slice(0, rowIndex), ...old.slice(rowIndex + 1)];

				if (projectBudget !== undefined && isFixedPrice) {
					const oldRow = old[rowIndex] as RoleRow;

					const newTotalExpensesRevenue =
						(groupedProjectTotals?.expensesTotals.baselineRevenue || 0) - oldRow.revenue;

					const multiplier = groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
						? (projectBudget - newTotalExpensesRevenue) / groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
						: 1;

					return renormaliseRoleRevenues(updatedRows, multiplier);
				}

				return updatedRows;
			});
		}
	};

	const handleChangeDeliverable = (newDeliverable: Deliverable) => {
		if (setPhaseDeliverables && deliverable) {
			setPhaseDeliverables(old => {
				const prevIndex = old.findIndex(item => item.id === phaseDeliverable?.id);
				const phaseDeliverableAlreadyExists = old.find(
					phaseDeliverable =>
						phaseDeliverable?.deliverable?.id === newDeliverable.id && phaseDeliverable?.phaseId === phase?.id
				);

				if (phaseDeliverableAlreadyExists) {
					return [...old.slice(0, prevIndex), ...old.slice(prevIndex + 1)];
				}

				const updatedPhaseDeliverable: ProjectPhaseDeliverable = {...old[prevIndex], deliverable: newDeliverable};

				return [...old.slice(0, prevIndex), updatedPhaseDeliverable, ...old.slice(prevIndex + 1)];
			});
		}
		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => {
				return old.map(baselineItem =>
					(deliverable?.id ? baselineItem.deliverableId === deliverable?.id : !baselineItem.deliverableId) &&
					baselineItem.phaseId === phase?.id
						? {...baselineItem, deliverableId: newDeliverable.id}
						: baselineItem
				);
			});
		}
	};

	const showChangeDeliverableModal = () => {
		showModal({
			type: MODAL_TYPE.CHANGE_DELIVERABLE_MODAL,
			projectDeliverables,
			currentDeliverable: deliverable,
			callback: handleChangeDeliverable,
		});
	};

	const handleDeleteDeliverable = () => {
		setPhaseDeliverables &&
			setPhaseDeliverables(old => {
				const prevIndex = old.findIndex(item => item.id === phaseDeliverable?.id);
				return [...old.slice(0, prevIndex), ...old.slice(prevIndex + 1)];
			});
		setProjectBaselineItems &&
			setProjectBaselineItems(old => {
				return old.filter(
					baselineItem => !(baselineItem.deliverableId === deliverable?.id && baselineItem.phaseId === phase?.id)
				);
			});
	};

	const showDeleteDeliverableModal = () => {
		showModal({
			type: MODAL_TYPE.REMOVE_DELIVERABLE_FROM_PHASE_MODAL,
			callback: handleDeleteDeliverable,
		});
	};

	const showManageDeliverablesModal = () => {
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: projectDeliverables,
			projectId: phase?.projectId,
			baselineItems,
		});
	};

	const contextMenuOptions: ContextMenuOption[] = editMode
		? [
				{
					name: formatMessage({id: 'deliverable.change_deliverable'}),
					onClick: showChangeDeliverableModal,
				},
				...(deliverable
					? [
							{
								name: formatMessage({id: 'deliverable.remove_deliverable_from_phase'}),
								onClick: showDeleteDeliverableModal,
							},
					  ]
					: []),
		  ]
		: [
				{
					name: formatMessage({id: 'deliverables.manage.modal.manage_deliverables'}),
					onClick: showManageDeliverablesModal,
				},
		  ];

	return (
		<NestedCard colorTheme="medium" dataCy={'deliverable-nested-card'}>
			<NestedCard.Header
				{...(deliverable
					? {
							mainIcon: 'deliverable',
							sectionName: formatMessage({id: 'common.deliverable'}),
					  }
					: {sectionName: formatMessage({id: 'deliverable.no_deliverable'})})}
				name={deliverable?.name || ''}
				showContextMenu={true}
				contextMenuOptions={contextMenuOptions}
			></NestedCard.Header>
			{!!baselineItems?.length && (
				<NestedCard.SummaryContent>
					<SummaryContainer justifyContent="center">
						<SummaryCard label={formatMessage({id: 'common.hours'})} value={hours} />
						<SummaryCard label={formatMessage({id: 'common.value_of_service'})} value={valueOfService} />
						<SummaryCard label={formatMessage({id: 'project_budget.revenue'})} value={revenue} />
						<SummaryCard label={formatMessage({id: 'common.cost'})} value={cost} />
						<SummaryCard label={formatMessage({id: 'common.profit'})} value={profit} />
						<SummaryCard label={formatMessage({id: 'common.margin'})} value={margin} />
					</SummaryContainer>
				</NestedCard.SummaryContent>
			)}
			<NestedCard.CollapsibleContentWrapper>
				<NestedCard.Content>
					<FlexColumn gap="m">
						<div style={{marginBottom: '16px'}}>
							<TableTitleWrapper>
								<Icon icon="deliverablesTimeEstimate" size="l"></Icon>
								<SubHeading size={'2'}>{timeEstimatesSubHeading}</SubHeading>
							</TableTitleWrapper>
							{hasTimeTable ? (
								<TimeEstimatesTable
									onDeleteRow={onDeleteRoleRow}
									onRoleSelect={onRoleSelect}
									recalculate={recalculateRoleRows}
									editMode={editMode}
									company={company}
									ref={rolesTableRef}
									tableData={rolesData}
									eyeOptionMap={eyeOptionMap}
									setTableData={setProjectBaselineItems}
								/>
							) : (
								<Frame centerContent={true}>
									<Text color="auxiliary">{formatMessage({id: 'deliverable.no_time_estimates'})}</Text>
								</Frame>
							)}
							{editMode && (
								<AddButton
									text={formatMessage({id: 'deliverable.add_hours_estimate'})}
									onClick={onRoleAdd}
								></AddButton>
							)}
						</div>
						<div>
							<TableTitleWrapper>
								<Icon icon="expensesNew" size="l"></Icon>
								<SubHeading size={'2'}>{expenseEstimatesSubHeading}</SubHeading>
							</TableTitleWrapper>
							{hasExpensesTable ? (
								<ExpensesEstimatesTable
									onDeleteRow={onDeleteExpenseRow}
									onCategorySelect={onExpenseCategorySelect}
									recalculateRow={recalculateExpenseRows}
									editMode={editMode}
									company={company}
									ref={expensesTableRef}
									tableData={expensesData}
									eyeOptionMap={eyeOptionMap}
									setTableData={setProjectBaselineItems}
								/>
							) : (
								<Frame centerContent={true}>
									<Text color="auxiliary">{formatMessage({id: 'deliverable.no_expense_estimates'})}</Text>
								</Frame>
							)}
							{editMode && (
								<AddButton
									text={formatMessage({id: 'deliverable.add_expenses_estimate'})}
									onClick={onExpenseAdd}
								></AddButton>
							)}
						</div>
					</FlexColumn>
				</NestedCard.Content>
			</NestedCard.CollapsibleContentWrapper>
		</NestedCard>
	);
};
